import { AppInfiniteTable } from "@/components/blocks/app-infinite-table";
import { SearchInput } from "@/components/elements/search-input";
import { PageBackButton } from "@/components/elements/page-back-button";
import { AppBreadcrumbs } from "@/components/layouts/app-breadcrumbs";
import { HeaderMenu } from "@/components/layouts/header-menu";
import PageLayout from "@/components/layouts/page.layout";
import { PAGE_META } from "@/config/page-meta";
import { RoutePaths } from "@/config/route-paths";
import { useAuth } from "@/contexts/auth";
import { useOrganizationFromListCache } from "@/features/organizations/queries/use-organization-from-list-cache";
import { EmptySeasonsList } from "@/features/seasons/pages/list/components/empty-seasons-list";
import { ErrorSeasonsList } from "@/features/seasons/pages/list/components/error-seasons-list";
import { FilteredEmptySeasonsList } from "@/features/seasons/pages/list/components/filtered-empty-seasons-list";
import { LoadingSeasonsList } from "@/features/seasons/pages/list/components/loading-seasons-list";
import { SeasonNameCell } from "@/features/seasons/pages/list/components/season-name-cell";
import {
  SeasonBooleanField,
  SeasonColumnHint,
  SeasonMatchLockTimeField,
  SeasonRosterField,
  SeasonUiTypeField,
} from "@/features/seasons/pages/list/components/season-settings-fields";
import { SeasonSettingsProvider } from "@/features/seasons/pages/list/hooks/use-season-settings";
import { useSeasonsListParams } from "@/features/seasons/pages/list/hooks/use-seasons-list-params";
import { getSeasonsListBreadcrumbs } from "@/features/seasons/pages/list/lib/get-seasons-list-breadcrumbs";
import { useSeasonsListQuery } from "@/features/seasons/queries/use-seasons-list";
import { usePageMeta } from "@/hooks/use-page-meta";
import { ListSection, ListSectionFilters, PageHeader, SectionWithHeading } from "@cmde/shadcn";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

const SEARCH_DEBOUNCE_MS = 300;

export default function SeasonListPage() {
  const { t } = useTranslation();
  const { customerName } = useAuth();
  const { customerId, organizationId, sport } = useSeasonsListParams();
  const organization = useOrganizationFromListCache(customerId, organizationId);
  const [inputValue, setInputValue] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    const timeoutId = window.setTimeout(() => setSearch(inputValue.trim()), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timeoutId);
  }, [inputValue]);

  const seasonsListQuery = useSeasonsListQuery({ sport, organizationId, search });
  const seasons = useMemo(
    () => seasonsListQuery.data?.pages.flatMap((page) => page ?? []) ?? [],
    [seasonsListQuery.data],
  );

  const breadcrumbs = useMemo(
    () =>
      getSeasonsListBreadcrumbs({
        sport,
        customerId,
        customerName,
        organizationId,
        organizationName: organization?.name ?? `Organization ${organizationId}`,
      }),
    [sport, customerId, customerName, organizationId, organization],
  );

  const uiTypeTooltip =
    sport === "hockey" ? t("season.ui-type-hockey-tooltip") : t("season.ui-type-tooltip");

  return (
    <>
      {usePageMeta(PAGE_META.seasons)}
      <PageLayout>
        <div className="h-full">
          <PageHeader
            title={t("seasons.list.messages.title")}
            subtitle={t("seasons.list.messages.subtitle")}
            backAction={<PageBackButton href={RoutePaths.organizations.list({ customerId })} />}
            breadcrumbs={<AppBreadcrumbs breadcrumbs={breadcrumbs} />}
            secondaryNavigation={<HeaderMenu />}
          />

          <SectionWithHeading heading="">
            <ListSection
              header={
                <ListSectionFilters
                  searchComponent={
                    <SearchInput
                      value={inputValue}
                      onChange={setInputValue}
                      placeholder={t("seasons.list.search.placeholder")}
                      disabled={seasonsListQuery.isLoading}
                    />
                  }
                />
              }
            >
              <SeasonSettingsProvider sport={sport} organizationId={organizationId} seasons={seasons}>
                <AppInfiniteTable
                  queryResult={seasonsListQuery}
                  getKey={(season) => season.seasonId}
                  loadingContent={<LoadingSeasonsList />}
                  emptyContent={search ? <FilteredEmptySeasonsList /> : <EmptySeasonsList />}
                  errorContent={
                    <ErrorSeasonsList
                      retry={() => seasonsListQuery.refetch()}
                      errorMessage={seasonsListQuery.error?.message}
                    />
                  }
                  columns={[
                    {
                      key: "name",
                      header: t("seasons.list.table.name"),
                      cell: (season) => <SeasonNameCell season={season} />,
                    },
                    {
                      key: "uiType",
                      header: <SeasonColumnHint label={t("seasons.list.table.ui_type")} tooltip={uiTypeTooltip} />,
                      cell: (season) => <SeasonUiTypeField season={season} sport={sport} />,
                    },
                    {
                      key: "defaultRoster",
                      header: (
                        <SeasonColumnHint
                          label={t("seasons.list.table.default_roster")}
                          tooltip={t("season.default-roster-tooltip")}
                        />
                      ),
                      cell: (season) => <SeasonRosterField season={season} />,
                    },
                    {
                      key: "allowRosterSelect",
                      header: (
                        <SeasonColumnHint
                          label={t("seasons.list.table.allow_roster")}
                          tooltip={t("season.allow-roster-tooltip")}
                        />
                      ),
                      cell: (season) => (
                        <SeasonBooleanField
                          season={season}
                          field="allowRosterSelect"
                          label={t("seasons.list.table.allow_roster")}
                        />
                      ),
                    },
                    {
                      key: "allowMatchOfficials",
                      header: (
                        <SeasonColumnHint
                          label={t("seasons.list.table.allow_officials")}
                          tooltip={t("season.allow-officials-tooltip")}
                        />
                      ),
                      cell: (season) => (
                        <SeasonBooleanField
                          season={season}
                          field="allowMatchOfficials"
                          label={t("seasons.list.table.allow_officials")}
                        />
                      ),
                    },
                    {
                      key: "allowMultipleCaptains",
                      header: (
                        <SeasonColumnHint
                          label={t("seasons.list.table.allow_multiple_captains")}
                          tooltip={t("season.allow-multiple-captains-tooltip")}
                        />
                      ),
                      cell: (season) => (
                        <SeasonBooleanField
                          season={season}
                          field="allowMultipleCaptains"
                          label={t("seasons.list.table.allow_multiple_captains")}
                        />
                      ),
                    },
                    {
                      key: "matchLockTime",
                      header: (
                        <SeasonColumnHint
                          label={t("seasons.list.table.match_lock_time")}
                          tooltip={t("season.matchLockTime-tooltip")}
                        />
                      ),
                      cell: (season) => <SeasonMatchLockTimeField season={season} />,
                    },
                  ]}
                />
              </SeasonSettingsProvider>
            </ListSection>
          </SectionWithHeading>
        </div>
      </PageLayout>
    </>
  );
}
